// The time zones a signed-in user last chose on the Program pages:
// unsafeMetadata.selectedTimeZones, 0 to 2 IANA zones, the latest first (e.g. ["Asia/Singapore",
// "Australia/Sydney"]). They follow the user to another device: the Program shows the first, and
// offers it beside the event's zone and the browser's (the second too, if that makes no more than
// three; Program/TimeZone.vue). Any choice made in the menu is recorded, the event's zone included.

import {ref} from "vue";
import {DateTime} from "luxon";

const MAX_ZONES = 2;
const STORAGE_KEY = "timeZoneSelected"; // this browser's last choice, signed out too
export const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const getIsZone = (zone) => typeof zone === "string" && zone.includes("/");

function getStoredTimeZone() {
  try {
    const zone = localStorage.getItem(STORAGE_KEY);
    return getIsZone(zone) ? zone : "";
  } catch {
    return "";
  }
}

// a choice made on this page (Program menu, Profile): reactive, so the banner, the /2026 dates
// and #profile's Events change at once, before the account's copy is saved (2026-10-13)
const chosenNow = ref("");

// The zone dates and times are shown in (decided 2026-10-13): a choice just made here, else the
// account's last choice (Program menu or Profile → Settings), else this browser's last choice,
// else the browser's own zone. The Program starts there (a zone in a link still wins); the /2026
// pages, the banner and #profile's Events follow it (useLocalEventDates.js).
export function getPreferredTimeZone(user) {
  const accountChoice = getSelectedTimeZones(user)[0];
  return chosenNow.value || accountChoice || getStoredTimeZone() || browserTimeZone || "UTC";
}

// a zone chosen in this browser (just now, else earlier), for links to #register (another site,
// which can't read this browser's choice): "" when none
export function getBrowserChoice() {
  return chosenNow.value || getStoredTimeZone();
}

// the zone the Program (or Profile) now shows: this page follows it at once, and this browser
// remembers it
export function setStoredTimeZone(zone) {
  if (getIsZone(zone)) chosenNow.value = zone;
  try {
    localStorage.setItem(STORAGE_KEY, zone);
  } catch {
    // private mode: the choice lasts for this page only
  }
}

// True if the two zones' UTC offsets differ on any day of the year. Comparing only today's
// offsets hid the option for e.g. Lagos (+1 all year) in September, when London is also +1,
// although in November (London +0) every session is an hour apart.
function getHasDifferentOffsets(zoneA, zoneB, year) {
  const start = DateTime.fromObject({year: Number(year), month: 1, day: 1}, {zone: zoneA});
  const end = start.plus({years: 1});
  for (let day = start; day < end; day = day.plus({days: 1})) {
    if (day.setZone(zoneB).offset !== day.offset) return true;
  }
  return false;
}

// The zones offered (the Program's menu, the /2026 intro's): at most three, the event's zone, the
// browser's (when it differs) and the user's last choice; the choice before that only when it
// doesn't make a fourth (it stays stored either way)
const MAX_OPTIONS = 3;
export function getZoneOptions({eventZone, user, year}) {
  const isOwnDifferent = eventZone && getHasDifferentOffsets(eventZone, browserTimeZone, year);
  const own = isOwnDifferent ? [browserTimeZone] : [];
  const [lastChoice, previousChoice] = getSelectedTimeZones(user);
  const zones = [...new Set([eventZone, ...own, lastChoice].filter(Boolean))];
  if (previousChoice && !zones.includes(previousChoice) && zones.length < MAX_OPTIONS) {
    zones.push(previousChoice);
  }
  return zones.sort();
}

export function getSelectedTimeZones(user) {
  const zones = user?.unsafeMetadata?.selectedTimeZones;
  return Array.isArray(zones) ? [...new Set(zones.filter(getIsZone))].slice(0, MAX_ZONES) : [];
}

// the chosen zone first, the one before it second (a zone never twice)
export function getNextTimeZones(zones, chosen) {
  return [chosen, ...zones.filter((zone) => zone !== chosen)].slice(0, MAX_ZONES);
}

// a zone chosen in a menu (the /2026 intro's; the Program's does the same and more): every page
// follows it at once, this browser remembers it, and the account too when signed in
export function chooseTimeZone(user, zone) {
  setStoredTimeZone(zone);
  return saveSelectedTimeZone(user, zone);
}

// signed in: records the choice (merged: Clerk replaces unsafeMetadata as a whole); never throws
export async function saveSelectedTimeZone(user, chosen) {
  if (!user?.update || !getIsZone(chosen)) return;
  const zones = getSelectedTimeZones(user);
  const next = getNextTimeZones(zones, chosen);
  if (next.join() === zones.join()) return;
  try {
    await user.update({unsafeMetadata: {...user.unsafeMetadata, selectedTimeZones: next}});
  } catch (error) {
    console.error(`selectedTimeZones: ${error}`);
  }
}
